import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

interface User {
    id: number;
    username: string;
    first_name: string;
    last_name: string;
    email: string;
}

interface PermissionsContextType {
    permissions: string[] | string;
    user: User | null;
    loading: boolean;
    hasPermission: (codename: string) => boolean;
    refreshPermissions: () => Promise<void>;
    login: (access: string, refresh: string) => Promise<void>;
    logout: () => void;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [permissions, setPermissions] = useState<string[] | string>([]);
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchPermissions = useCallback(async () => {
        const token = localStorage.getItem('access');
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            const response = await api.get('users/me/');
            setPermissions(response.data.permissions || []);
            setUser(response.data);
        } catch (error) {
            console.error("Error fetching permissions", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPermissions();
    }, [fetchPermissions]);

    const login = useCallback(async (access: string, refresh: string) => {
        localStorage.setItem('access', access);
        localStorage.setItem('refresh', refresh);
        await fetchPermissions();
    }, [fetchPermissions]);

    const hasPermission = (codename: string) => {
        if (permissions === 'all') return true;
        if (Array.isArray(permissions)) {
            // Django returns permissions in 'app_label.codename' format.
            return permissions.some(p => p === codename || p.endsWith('.' + codename));
        }
        return false;
    };

    const logout = useCallback(() => {
        localStorage.removeItem('access');
        localStorage.removeItem('refresh');
        setUser(null);
        setPermissions([]);
        window.location.href = '/login';
    }, []);

    return (
        <PermissionsContext.Provider value={{ permissions, user, loading, hasPermission, refreshPermissions: fetchPermissions, login, logout }}>
            {children}
        </PermissionsContext.Provider>
    );
};

export const usePermissions = () => {
    const context = useContext(PermissionsContext);
    if (context === undefined) {
        throw new Error('usePermissions must be used within a PermissionsProvider');
    }
    return context;
};
